import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Pagination, Table } from '@ui-modules';
import { DATE_FORMAT } from '@common/constants';
import { IAgencyInvoiceDataApi } from '@common/interfaces';
import { DeleteBlock } from '@components';
import './styles.scss';

interface ITripsDetails {
  data?: IAgencyInvoiceDataApi;
  selectedPage: number;
  deleteTrip: (id: string) => void;
  onPageChange: (page: number) => void;
}

interface ITableData {
  actions: JSX.Element;
  agency: string;
  cost: string;
  driver: string;
  duration: string;
  green_fund: string;
  mileage: number;
  passenger: string;
  pax: string;
  pickup_date: string;
  requesting_unit: string;
  transfer_type: string;
  vehicle: string;
}

const TripsDetails: FC<ITripsDetails> = ({ data, selectedPage, deleteTrip, onPageChange }) => {
  const { t } = useTranslation();

  const [tripsDetails, setTripsDetails] = useState<ITableData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  useEffect(() => {
    if (data?.drives.results.length) {
      const tableData = data?.drives.results.map((drive) => {
        const {
          booking_agency,
          booking_first_name,
          booking_last_name,
          cost,
          driver_first_name,
          driver_last_name,
          green_fund,
          id,
          pickup_date,
          vehicle_label,
        } = drive;

        return {
          ...drive,
          actions: (
            <DeleteBlock
              id={id}
              key={id}
              deleteAction={(driveId) => deleteTrip(driveId as string)}
            />
          ),
          agency: booking_agency || '',
          cost: cost ? `$${cost}` : '$0',
          driver: `${driver_first_name} ${driver_last_name}`,
          green_fund: green_fund ? `$${green_fund}` : '$0',
          passenger: `${booking_first_name} ${booking_last_name}`,
          pickup_date: moment(pickup_date, DATE_FORMAT.API_ALT).format(DATE_FORMAT.MOMENT),
          vehicle: vehicle_label || '',
        };
      });

      setTripsDetails(tableData);
      setTotalItems(data.drives.count);
    } else {
      setTripsDetails([]);
      setTotalItems(0);
    }
  }, [data, deleteTrip]);

  const columns = useMemo(
    () => [
      {
        key: 'pickup_date',
        dataIndex: 'pickup_date',
        title: 'Date',
      },
      {
        key: 'passenger',
        dataIndex: 'passenger',
        title: t('common.passenger'),
      },
      {
        key: 'agency',
        dataIndex: 'agency',
        title: t('common.agency'),
      },
      {
        key: 'requesting_unit',
        dataIndex: 'requesting_unit',
        title: t('common.unit'),
      },
      {
        key: 'pax',
        dataIndex: 'pax',
        title: 'PAX',
      },
      {
        key: 'transfer_type',
        dataIndex: 'transfer_type',
        title: t('common.transferType'),
      },
      {
        key: 'vehicle',
        dataIndex: 'vehicle',
        title: t('common.vehicle'),
      },
      {
        key: 'driver',
        dataIndex: 'driver',
        title: t('common.driver'),
      },
      {
        key: 'duration',
        dataIndex: 'duration',
        title: t('common.duration'),
      },
      {
        key: 'mileage',
        dataIndex: 'mileage',
        title: 'Km',
      },
      {
        key: 'cost',
        dataIndex: 'cost',
        title: `${t('common.price')} $`,
      },
      {
        key: 'green_fund',
        dataIndex: 'green_fund',
        title: `${t('common.greenFund')} $`,
      },
      {
        key: 'actions',
        dataIndex: 'actions',
        title: '',
      },
    ],
    [t],
  );

  return (
    <section className="container">
      <div className="table">
        <Table columns={columns} data={tripsDetails} />
      </div>

      {totalItems > 10 && (
        <div className="row">
          <Pagination
            selectedPage={selectedPage}
            showJumper
            totalPages={totalItems}
            onPageChange={(selected: number): void => onPageChange(selected)}
          />
        </div>
      )}
    </section>
  );
};

export default TripsDetails;
